/**
 * PanelStage renders the shell's ordered pixel slots. The allocator owns
 * widths; CSS receives pixel geometry from the stage's left edge, so a stage
 * that changes width beneath the row moves no fixed-width panel by itself.
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';
import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
    overlayPanelsAtom, stagePanelsAtom, visiblePanelsAtom,
} from '@/state/ui/shellPanelsAtoms';
import {
    resetPanelWidthAtom, setPanelWidthsAtom, userWidthsAtom,
} from '@/state/ui/shellRecordAtoms';
import { isSmViewportAtom, isStageResizingAtom, stageWidthAtom } from '@/state/ui/shellStageAtoms';
import type {
    AllocatedPanel, PanelHostBindings, PanelId, PlacedResizeSlot, ResizeSlot, StagePresence,
} from '@/types/panels';
import { MAX_PANEL_WIDTH, PanelTransitionState } from '@/types/panels';

import {
    allocate, allocatePhone, DEFAULT_PANEL_ORDER, getPanelWidthBudget, PANEL_GAP_PX,
    PANEL_PADDING_PX,
    PHONE_PADDING_PX,
} from './allocate';
import { planDocking, planExpansion, planWidening } from './panelExpand';
import type { SlotStyle } from './panelGeometry';
import { getOverlaySlotStyle, getPanelSlotStyle } from './panelGeometry';
import { PanelHost } from './PanelHost';
import { PanelResizeHandle } from './PanelResizeHandle';
import { panels as registry } from './registry';
import {
    beginStageAnimation,
    STAGE_ANIMATION_MARGIN_MS,
    usePanelTransitionOver,
} from './stageMotion';

const ENTER_EXIT_MS = 200;
const MOUNT_TO_APPEAR_MS = 20;
// Lets the exit transition finish before its node is unmounted.
const EXIT_UNMOUNT_MARGIN_MS = 20;

/** An allocated panel with the CSS geometry its slot renders at. */
type StagePanel = AllocatedPanel & { slotStyle: SlotStyle };

/** The slot geometry per panel id, as `startPx|widthPx`. */
type GeometrySignature = Map<string, string>;

/** Shell state a panel's content reads beyond its own props. */
interface StageContext {
    settingsTab?: unknown;
}

/** What orders a host in the DOM. */
interface StageHost {
    id: string;
    config: { order?: number };
}

/** The timer that moves a panel on from the presence state it was set for. */
interface PresenceTimer {
    state: PanelTransitionState;
    timer: ReturnType<typeof setTimeout>;
}

export function advancePanelPresence(state: PanelTransitionState): PanelTransitionState {
    if (state === PanelTransitionState.Entering) {
        return PanelTransitionState.Appearing;
    }
    if (state === PanelTransitionState.Appearing) {
        return PanelTransitionState.Stable;
    }
    return state;
}

/**
 * Fold a new allocation into the stage: a new panel enters, a departed one exits from its last
 * place, and every panel still allocated takes its new geometry. A panel in `floatingIds` floated
 * over the row until now: it is on screen already, so it moves into its slot with no entrance.
 */
export function reconcilePanelPresence<T extends { id: string }>(
    previous: StagePresence<T>, items: T[], floatingIds: ReadonlySet<string> = new Set(),
): StagePresence<T> {
    const previousIds = new Set(previous.items.map((item) => item.id));
    const nextIds = new Set(items.map((item) => item.id));
    const states = { ...previous.states };
    for (const item of items) {
        if (floatingIds.has(item.id)) {
            states[item.id] = PanelTransitionState.Stable;
        } else if (!previousIds.has(item.id)) {
            states[item.id] = PanelTransitionState.Entering;
        } else if (states[item.id] === PanelTransitionState.Exiting) {
            states[item.id] = PanelTransitionState.Appearing;
        }
    }
    for (const item of previous.items) {
        if (!nextIds.has(item.id)) {
            states[item.id] = PanelTransitionState.Exiting;
        }
    }
    return { source: items, items: retainExitingPanelItems(previous.items, items), states };
}

/** Move a panel on from `state` once its time is up: an exit ends by removing it. */
export function advancePanelTransition<T extends { id: string }>(
    previous: StagePresence<T>, id: string, state: PanelTransitionState,
): StagePresence<T> {
    if (previous.states[id] !== state) {
        return previous;
    }
    const states = { ...previous.states };
    if (state !== PanelTransitionState.Exiting) {
        states[id] = advancePanelPresence(state);
        return { ...previous, states };
    }
    delete states[id];
    return { ...previous, items: previous.items.filter((item) => item.id !== id), states };
}

function getPresenceDelayMs(state: PanelTransitionState): number {
    if (state === PanelTransitionState.Entering) {
        return MOUNT_TO_APPEAR_MS;
    }
    return state === PanelTransitionState.Exiting
        ? ENTER_EXIT_MS + EXIT_UNMOUNT_MARGIN_MS
        : ENTER_EXIT_MS;
}

/**
 * The order hosts sit in the DOM: the row's own order, then the id. It never changes for a pair of
 * panels, because React moves a node whose key changes place, and a moved node drops its running
 * transition and snaps to its new slot while the panels beside it ease to theirs.
 */
export function compareHostOrder(firstStageHost: StageHost, second: StageHost): number {
    const getRowOrder = (host: StageHost): number => host.config.order ?? DEFAULT_PANEL_ORDER;
    return getRowOrder(firstStageHost) - getRowOrder(second) ||
        firstStageHost.id.localeCompare(second.id);
}

export function canStartPanelHeavyWork(
    state: PanelTransitionState, isPanelTransitionOver: boolean,
): boolean {
    return state === PanelTransitionState.Stable && isPanelTransitionOver;
}

export function getGeometrySignature(
    items: { id: string; startPx: number; widthPx: number }[],
): GeometrySignature {
    return new Map(items.map((item) => [item.id, `${item.startPx}|${item.widthPx}`]));
}

/** Bind physical CSS geometry to the allocation that produced each item. */
export function materializePanelGeometry(
    items: AllocatedPanel[], paddingPx: number,
): StagePanel[] {
    return items.map((item) => ({
        ...item,
        slotStyle: getPanelSlotStyle(item, paddingPx),
    }));
}

/** Current items lead; departing items retain their last materialized style. */
export function retainExitingPanelItems<T extends { id: string }>(
    previousItems: T[], currentItems: T[],
): T[] {
    const currentIds = new Set(currentItems.map((item) => item.id));
    return [
        ...currentItems,
        ...previousItems.filter((item) => !currentIds.has(item.id)),
    ];
}

export function doesPanelHaveFollowingSlot(items: { id: string }[], panelId: string): boolean {
    const index = items.findIndex((item) => item.id === panelId);
    return index >= 0 && index < items.length - 1;
}

/**
 * The row the handle at `boundaryIndex` works on: the allocated panels, then
 * the space the allocator left over. That last slot has no panel behind it — a
 * drag pushes into it and pulls back out of it, which is how a panel claims
 * trailing room without the allocator handing it out (see allocate.ts). `fill`
 * marks every slot with no width of its own to remember.
 *
 * An expanded panel is that remainder to every handle but the one on its own
 * right edge: a neighbour's drag leaves it filling, its own drag sets its width.
 */
export function handleRow(
    items: AllocatedPanel[], stageWidth: number, boundaryIndex: number,
): ResizeSlot[] {
    const used = items.reduce((sum, item) => sum + item.widthPx, 0);
    return [
        ...items.map((item, index) => ({
            id: item.id,
            title: item.config.title,
            widthPx: item.widthPx,
            min: item.min,
            max: item.max,
            fill: item.isExpanded ? index !== boundaryIndex - 1 : item.fill,
        })),
        {
            id: null,
            title: null,
            widthPx: Math.max(0, getPanelWidthBudget(stageWidth, items.length) - used),
            min: 0,
            max: Infinity,
            fill: true,
        },
    ];
}

/**
 * Boundary index per handle: one at every panel's right edge, the last panel
 * included so the trailing space always has a grip. The exception is a fill
 * panel in that position — it is the row's remainder, with nothing to drag it
 * against.
 */
export function resizeBoundaries(items: { fill: boolean }[]): number[] {
    return items
        .map((item, index) => (index === items.length - 1 && item.fill ? -1 : index + 1))
        .filter((boundary) => boundary > 0);
}

export function hasPanelPresenceChange(
    previous: GeometrySignature | null, nextGeometrySignature: GeometrySignature,
): boolean {
    if (previous === null) {
        return false;
    }
    if (previous.size !== nextGeometrySignature.size) {
        return true;
    }
    for (const id of nextGeometrySignature.keys()) {
        if (!previous.has(id)) {
            return true;
        }
    }
    return false;
}

function hasRemainingPanelGeometryChange(
    previous: GeometrySignature | null, nextGeometrySignature: GeometrySignature,
): boolean {
    if (previous === null) {
        return false;
    }
    for (const [id, signature] of nextGeometrySignature) {
        const before = previous.get(id);
        if (before !== undefined && before !== signature) {
            return true;
        }
    }
    return false;
}

interface PanelStageProps {
    bindings: PanelHostBindings;
    context?: StageContext;
    /** What the stage draws while it holds no panel: the home launcher. */
    empty?: ReactNode;
    isInsetTop?: boolean;
    /** The chrome moves the stage's edge: the nav drag or the chat slide. */
    isLiveResize?: boolean;
}

export const PanelStage = ({
    bindings,
    context,
    empty: emptyContent,
    isInsetTop = false,
    isLiveResize: isChromeResizing = false,
}: PanelStageProps): ReactElement => {
    const visiblePanels = useAtomValueRawSync(visiblePanelsAtom);
    // The overlay panels the row has no room for, floated over it.
    const overlayPanels = useAtomValueRawSync(overlayPanelsAtom);
    const stagePanels = useAtomValueRawSync(stagePanelsAtom);
    const stageWidth = useAtomValueRawSync(stageWidthAtom);
    const isSmViewport = useAtomValueRawSync(isSmViewportAtom);
    const isStageResizing = useAtomValueRawSync(isStageResizingAtom);
    const userWidths = useAtomValueRawSync(userWidthsAtom);
    const setPanelWidths = useSetAtom(setPanelWidthsAtom);
    const resetPanelWidth = useSetAtom(resetPanelWidthAtom);
    const isLiveResize = isChromeResizing || isStageResizing;
    const { onClose } = bindings;
    const stageRef = useRef<HTMLElement | null>(null);
    const isPanelTransitionOver = usePanelTransitionOver();

    const items = useMemo(() => {
        const allocatedPanels = isSmViewport
            ? allocatePhone(visiblePanels, stageWidth)
            : allocate({ openPanels: visiblePanels, stageWidth, userWidths });
        return materializePanelGeometry(allocatedPanels, isSmViewport ? PHONE_PADDING_PX
            : PANEL_PADDING_PX);
    }, [visiblePanels, stageWidth, isSmViewport, userWidths]);

    // The geometry of the last commit. It changes at each resize sample, and its change must not
    // render the stage again.
    const geomRef = useRef<GeometrySignature | null>(null);
    const nextGeometry = useMemo(() => getGeometrySignature(items), [items]);
    const willPanelPresenceChange = !isSmViewport &&
        // eslint-disable-next-line react-hooks/refs -- the geometry of the last commit
        hasPanelPresenceChange(geomRef.current, nextGeometry);
    const willRemainingPanelGeometryAnimate = !isSmViewport &&
        // eslint-disable-next-line react-hooks/refs -- the geometry of the last commit
        hasRemainingPanelGeometryChange(geomRef.current, nextGeometry);
    const willLayoutAnimate = willPanelPresenceChange && willRemainingPanelGeometryAnimate;

    useLayoutEffect((): void => {
        const previous = geomRef.current;
        geomRef.current = nextGeometry;
        if (isSmViewport || isLiveResize || previous === null) {
            return;
        }
        if (hasRemainingPanelGeometryChange(previous, nextGeometry)) {
            // Anchored HERE, at the commit that starts the ease — not at whatever
            // earlier moment asked for the layout change. A heavy panel can push
            // this commit tens of milliseconds past the toggle that caused it, and
            // a window opened back there would expire mid-animation: content would
            // re-lay-out while its slot was still moving.
            //
            // `panelTransition: true` for every animated geometry change, not just
            // panels entering or leaving. Content freezes ride that channel, and a
            // freeze has to outlast the ease regardless of what set it in motion — a
            // chat toggle moves the same edges an opening sibling does.
            beginStageAnimation(ENTER_EXIT_MS + STAGE_ANIMATION_MARGIN_MS, {
                panelTransition: true,
            });
        }
    }, [nextGeometry, isSmViewport, isLiveResize]);

    // The released layout is recorded as the baseline BEFORE React commits the
    // same pixels, so that commit reads as no geometry change and starts no
    // ease. The free slot is dropped: it is not a panel and has no signature.
    const commitDragWidths = useCallback(
        (updates: Record<string, number>, layout: PlacedResizeSlot[]): void => {
            geomRef.current = getGeometrySignature(layout.filter(
                (slot): slot is PlacedResizeSlot & { id: string } => slot.id !== null));
            setPanelWidths(updates);
        }, [setPanelWidths]);

    // Expand: close whatever sits to a panel's right and let it fill the room
    // that frees up, now and whenever it opens again (app/panelExpand.ts).
    // PanelHost binds it per panel onto PanelContext.
    const expandPanel = useCallback((id: PanelId): void => {
        const plan = planExpansion(items, stageWidth, id);
        if (!plan?.canExpand) {
            return;
        }
        plan.closeIds.forEach((closeId) => onClose(closeId));
        setPanelWidths({ [id]: MAX_PANEL_WIDTH });
    }, [items, stageWidth, onClose, setPanelWidths]);

    // Exiting slots remain at their last committed geometry until their fade
    // completes. New slots mount invisible, appear, then become content-ready.
    const [render, setRender] = useState((): StagePresence<StagePanel> => ({
        source: items,
        items,
        states: Object.fromEntries(items.map((item) => [item.id, PanelTransitionState.Stable])),
    }));
    // The overlays of the last render: one that docks now joins the row from where it floated.
    const overlayIds = useMemo(
        (): ReadonlySet<string> => new Set(overlayPanels.map((panel) => panel.id)),
        [overlayPanels],
    );
    const [floatingIds, setFloatingIds] = useState(overlayIds);
    // Reconciled while rendering, so the commit paints the allocation it computed. On a stage
    // changing width every frame, a width a frame behind the stage's edge makes every panel
    // slide first and resize after.
    if (render.source !== items) {
        setRender(reconcilePanelPresence(render, items, floatingIds));
    }
    if (floatingIds !== overlayIds) {
        setFloatingIds(overlayIds);
    }

    const presenceTimers = useRef(new Map<string, PresenceTimer>());
    useEffect((): void => {
        const timers = presenceTimers.current;
        for (const [id, pendingPresenceTimer] of timers) {
            if (render.states[id] !== pendingPresenceTimer.state) {
                clearTimeout(pendingPresenceTimer.timer);
                timers.delete(id);
            }
        }
        for (const [id, state] of Object.entries(render.states)) {
            if (state === PanelTransitionState.Stable || timers.has(id)) {
                continue;
            }
            const timer = setTimeout((): void => {
                timers.delete(id);
                setRender((previous) => advancePanelTransition(previous, id, state));
            }, getPresenceDelayMs(state));
            timers.set(id, { state, timer });
        }
    }, [render.states]);

    useEffect(() => (): void => {
        const timers = presenceTimers.current;
        for (const { timer } of timers.values()) {
            clearTimeout(timer);
        }
        timers.clear();
    }, []);

    // An overlay is sized like a panel alone on the stage, at the stage's right edge.
    const overlayItems = useMemo((): StagePanel[] => overlayPanels
        .filter((panel) => registry[panel.id] !== undefined)
        .flatMap((panel) => allocate({ openPanels: [panel], stageWidth, userWidths }))
        .map((slot) => ({ ...slot, slotStyle: getOverlaySlotStyle(slot.widthPx, stageWidth) })),
    [overlayPanels, stageWidth, userWidths]);

    // Widen: grow a panel into the free room at the right end of the row (app/panelExpand.ts).
    // A floating overlay covers that room, so the row has none while one floats.
    const widenPanel = useCallback((id: PanelId, byPx: number): boolean => {
        if (isSmViewport || overlayItems.length > 0) {
            return false;
        }
        const widthPx = planWidening(items, stageWidth, id, byPx);
        if (widthPx === null) {
            return false;
        }
        setPanelWidths({ [id]: widthPx });
        return true;
    }, [isSmViewport, overlayItems, items, stageWidth, setPanelWidths]);

    // Pin: narrow the panels to the left of a floating overlay until the row docks it
    // (app/panelExpand.ts).
    const dockPlans = useMemo(() => new Map(overlayItems.map((overlay) => (
        [overlay.id, planDocking(items, overlay, stageWidth)]
    ))), [overlayItems, items, stageWidth]);
    const dockPanel = useCallback((id: PanelId): void => {
        const widths = dockPlans.get(id);
        if (widths) {
            setPanelWidths(widths);
        }
    }, [dockPlans, setPanelWidths]);

    // The panels the row has no room for stay mounted behind it, hidden, so
    // what the operator had in them — a half-edited form, a scroll position —
    // is there when the row hands them back. The phone has no presence
    // machine: its one panel is shown as soon as it is chosen. A panel that
    // left the row for an overlay does not also fade out of it.
    const shownItems = useMemo((): StagePanel[] => {
        const overlayIds = new Set(overlayItems.map((item) => item.id));
        return (isSmViewport ? items : render.items).filter((item) => !overlayIds.has(item.id));
    }, [isSmViewport, items, render.items, overlayItems]);
    const hiddenItems = useMemo((): StagePanel[] => {
        const shownIds = new Set([...shownItems, ...overlayItems].map((item) => item.id));
        return stagePanels
            .filter((panel) => (
                registry[panel.id] !== undefined && panel.config.shouldKeepMounted &&
                !shownIds.has(panel.id)
            ))
            .map((panel) => materializePanelGeometry(
                isSmViewport
                    ? allocatePhone([panel], stageWidth)
                    : allocate({ openPanels: [panel], stageWidth, userWidths }),
                isSmViewport ? PHONE_PADDING_PX : PANEL_PADDING_PX,
            )[0])
            .filter((panel): panel is StagePanel => panel !== undefined);
    }, [isSmViewport, shownItems, overlayItems, stagePanels, stageWidth, userWidths]);

    const currentItemIds = useMemo(() => new Set(items.map((item) => item.id)), [items]);

    const renderHost = (
        item: StagePanel, state: PanelTransitionState, isHidden = false, hasPanelToRight = false,
        isOverlay = false,
    ): ReactElement | null => {
        const { id, config, props } = item;
        const Component = registry[id]?.Component;
        if (!Component) {
            return null;
        }

        let extraProps: Record<string, unknown> | null = null;
        if (id === 'camera_view') {
            extraProps = {
                settingsTab: context?.settingsTab,
                heavyStartupReady: canStartPanelHeavyWork(state, isPanelTransitionOver),
            };
        }

        return (
            <PanelHost
                key={id}
                id={id}
                config={config}
                bindings={bindings}
                expandPanel={expandPanel}
                canExpand={!isSmViewport && !!planExpansion(items, stageWidth, id)?.canExpand}
                widenPanel={widenPanel}
                dockPanel={isOverlay ? dockPanel : undefined}
                canDock={!!dockPlans.get(id)}
                transitionState={state}
                // An overlay moves with no row, so nothing holds its content back.
                willLayoutAnimate={!isOverlay && (willLayoutAnimate || !currentItemIds.has(id))}
                isPanelTransitionOver={isPanelTransitionOver}
                hasPanelToRight={hasPanelToRight}
                isOverlay={isOverlay}
                style={{
                    ...item.slotStyle,
                    ...(isHidden && { display: 'none' }),
                }}
            >
                <Component
                    {...props}
                    {...extraProps}
                />
            </PanelHost>
        );
    };

    const isTransitionOver = !Object.values(render.states)
        .some((state) => state !== PanelTransitionState.Stable);
    const handles = !isSmViewport && isTransitionOver
        ? resizeBoundaries(items).map((boundaryIndex) => {
            const left = items[boundaryIndex - 1];
            if (left === undefined) { return null; }
            return (
                <PanelResizeHandle
                    key={`${left.id}|${items[boundaryIndex]?.id ?? 'free'}`}
                    panels={handleRow(items, stageWidth, boundaryIndex)}
                    boundaryIndex={boundaryIndex}
                    boundaryStartPx={left.startPx + left.widthPx + PANEL_GAP_PX}
                    stageWidth={stageWidth}
                    stageRef={stageRef}
                    commitDragWidths={commitDragWidths}
                    resetPanelWidth={resetPanelWidth}
                />
            );
        })
        : null;

    return (
        <main
            ref={stageRef}
            data-inset-top={isInsetTop || undefined}
            data-resizing={isLiveResize || undefined}
            className="relative h-full min-h-0 w-full overflow-hidden"
        >
            {shownItems.length === 0 && emptyContent}
            {/* ONE keyed list for shown and hidden hosts: a panel crossing between
                the two keeps its instance, which is the whole point of keeping it. */}
            {[
                ...shownItems.map((item) => ({ item, hidden: false, overlay: false })),
                ...hiddenItems.map((item) => ({ item, hidden: true, overlay: false })),
                ...overlayItems.map((item) => ({ item, hidden: false, overlay: true })),
            ]
                .sort((a, b) => compareHostOrder(a.item, b.item))
                .map(({ item, hidden: isHidden, overlay: isOverlay }) => (isHidden || isOverlay
                    ? renderHost(item, PanelTransitionState.Stable, isHidden, false, isOverlay)
                    : renderHost(
                        item,
                        isSmViewport
                            ? PanelTransitionState.Stable
                            : render.states[item.id] ?? PanelTransitionState.Stable,
                        false,
                        !isSmViewport && doesPanelHaveFollowingSlot(shownItems, item.id),
                    )))}
            {handles}
        </main>
    );
};
